import axios from 'axios';
import { FC, SyntheticEvent, useRef, useState } from 'react';

import Flex from 'quarks/Flex';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import TextField from 'molecules/TextField/TextField';

import ComponentButton from 'components/Button/ComponentButton';

import { validateEmail } from 'utils/functions';

export type SubscribeFormProps = FlexProps & {
  heading?: string;
};

const SubscribeForm: FC<SubscribeFormProps> = ({ heading }) => {
  const [hasTyped, setHasTyped] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [validated, setValidated] = useState<boolean>(false);
  const [isShowMessage, setIsShowMessage] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.SyntheticEvent) => {
    if (e?.target) {
      const target = e?.target as HTMLInputElement;
      const updateData = {
        [target.name]: target.value,
      };
      setFormData(updateData);
      if (!hasTyped) {
        setHasTyped(true);
      }
    }
  };

  const handleBlur = () => {
    setValidated(!(formData.email && validateEmail(formData.email)));
  };

  const submitToHubspot = () => {
    const url =
      'https://api.hsforms.com/submissions/v3/integration/submit/2731810/7c327b33-37c2-483b-8e0f-708a02865b32';
    const utmData = {
      utm_campaign: window.sessionStorage.getItem('utm_campaign'),
      utm_source: window.sessionStorage.getItem('utm_source'),
      utm_medium: window.sessionStorage.getItem('utm_medium'),
      utm_term: window.sessionStorage.getItem('utm_term'),
      utm_content: window.sessionStorage.getItem('utm_content'),
      google_click_id: window.sessionStorage.getItem('gclid'),
    } as const;
    type utmType = keyof typeof utmData;

    const fieldsData: { name: string; value: string }[] = [];
    type formDataType = keyof typeof formData;

    Object.keys(formData).forEach(key => {
      if (!key.includes('gdpr_')) {
        fieldsData.push({ name: key, value: formData[key as formDataType] });
      }
    });

    Object.keys(utmData).forEach(key => {
      if (utmData[key as utmType] != null) {
        fieldsData.push({ name: key, value: utmData[key as utmType] || '' });
      }
    });
    const postData = {
      fields: fieldsData,
      submittedAt: Date.now(),
      context: {
        pageUri: document.URL,
        pageName: document.title,
      },
    };
    axios
      .post(url, postData, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(() => {
        setIsShowMessage(true);
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err));
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setIsShowMessage(false);

    // check honeypot field
    const honeypot = formRef?.current?.querySelector('[name="hp123"]');

    if (honeypot?.value) {
      return false;
    }

    if (!(formData.email && validateEmail(formData.email))) {
      setValidated(true);

      return false;
    }
    setValidated(false);
    submitToHubspot();
  };

  return (
    <Flex
      ref={formRef}
      flexDirection="column"
      width="100%"
      height="fit-content"
      backgroundColor="navy-900"
      borderRadius="20px"
      paddingX={24}
      paddingY={40}
      md={{ paddingAll: 64, gap: '24px' }}
      lg={{ paddingX: 32, paddingY: 40, gap: '24px' }}
      gap="16px"
    >
      <Text
        textStyle="textLg"
        fontWeight="semiBold"
        textColor="common-white"
        sm={{ fontSize: 'h5', lineHeight: 'h5' }}
        lg={{ fontSize: 'textLg', lineHeight: 'textLg' }}
      >
        {heading}
      </Text>
      {!isShowMessage ? (
        <Flex flexDirection="column" gap="12px" width="100%" maxWidth="100%">
          <TextField
            type="email"
            name="email"
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your Email"
            css={{
              width: '100%',
              maxWidth: '100%',
              minWidth: '100%',
              '&& .MuiInputBase-root': {
                maxWidth: '100%',
                minWidth: '100%',
                border: validated ? '1px solid #f00' : '1px solid #D0D5DD',
              },
            }}
          />
          <TextField type="text" name="hp123" display="none" onChange={handleChange} />
          <Flex onClick={handleSubmit} width="100%">
            <ComponentButton
              label="Subscribe"
              isButton
              css={{
                '&.MuiButtonBase-root': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                },
              }}
            />
          </Flex>
          <Flex
            marginTop={16}
            transition="opacity ease-in 0.5s"
            textColor="common-white"
            fontSize="textXs"
            lineHeight="textXs"
            {...(hasTyped ? { visibility: 'visible', opacity: 1 } : { height: 0, visibility: 'hidden', opacity: 0 })}
          >
            <p>
              FieldRoutes needs the contact information you provide to us to contact you about our products and
              services. You may unsubscribe from these communications at any time. For information on how to
              unsubscribe, as well as our privacy practices and commitment to protecting your privacy, please review our{' '}
              <Link textColor="common-white" href="/privacy-policy">
                Privacy Policy
              </Link>
              .
            </p>
          </Flex>
        </Flex>
      ) : (
        <Flex fontWeight="bold" textColor="common-white">
          Thanks for submitting the form
        </Flex>
      )}
    </Flex>
  );
};

export default SubscribeForm;

SubscribeForm.defaultProps = {
  heading: 'Subscribe to our weekly email newsletter',
};
